import { getTotalStars } from './collection.js';
import { getCompletedLevelIds } from './progress.js';

const PLAYER_KEY = 'color-dots-player:v1';

export interface PlayerAuth {
  playerId: string;
  token: string;
  nickname: string;
}

export interface SyncResult {
  totalStars: number;
  levelsCompleted: number;
  rank: number;
}

export function getStoredPlayer(): PlayerAuth | null {
  try {
    const raw = localStorage.getItem(PLAYER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlayerAuth>;
    if (
      typeof parsed.playerId === 'string' &&
      typeof parsed.token === 'string' &&
      typeof parsed.nickname === 'string'
    ) {
      return parsed as PlayerAuth;
    }
    return null;
  } catch {
    return null;
  }
}

function storePlayer(player: PlayerAuth): void {
  localStorage.setItem(PLAYER_KEY, JSON.stringify(player));
}

function clearStoredPlayer(): void {
  localStorage.removeItem(PLAYER_KEY);
}

/** No password/email — a nickname claims a slot server-side and a random
 *  token (never re-derivable, only returned this once) proves it's still the
 *  same device on later syncs. Fits a casual leaderboard; not meant to
 *  survive a determined account-theft attempt. */
export async function registerPlayer(nickname: string): Promise<PlayerAuth> {
  const response = await fetch('/api/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nickname }),
  });

  const body = (await response.json().catch(() => ({}))) as { error?: string } & Partial<PlayerAuth>;
  if (!response.ok || typeof body.playerId !== 'string') {
    if (response.status === 409) throw new Error('Этот ник уже занят — попробуй другой');
    if (response.status === 400) throw new Error('Ник должен быть 2–20 символов: буквы, цифры, пробелы');
    throw new Error('Не удалось зарегистрироваться, попробуй позже');
  }

  const player: PlayerAuth = {
    playerId: body.playerId,
    token: body.token as string,
    nickname: body.nickname as string,
  };
  storePlayer(player);
  return player;
}

/** Pushes the player's current lifetime stars to the leaderboard. Fire-and-
 *  forget from the caller's point of view — returns null (not an error) when
 *  no player is registered yet, so callers don't need their own guard. */
export async function syncScore(): Promise<SyncResult | null> {
  const player = getStoredPlayer();
  if (!player) return null;

  const response = await fetch('/api/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      playerId: player.playerId,
      token: player.token,
      totalStars: getTotalStars(),
      levelsCompleted: getCompletedLevelIds().size,
    }),
  });

  if (response.status === 401) {
    // Token no longer matches (e.g. storage half-cleared) — the nickname is
    // still claimed server-side, so don't silently re-register under it.
    clearStoredPlayer();
    return null;
  }
  if (!response.ok) return null;

  return (await response.json()) as SyncResult;
}
